import type { EProductType } from '@quesmed/types/models';
import type { IDropdownOptionsData } from '@quesmed/types/resolvers/query/admin';
import { DROPDOWN_OPTIONS, type IDropdownOptionsVar } from '@quesmed/types/resolvers/query/admin';
import { useMemo, useState } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import { useQuery } from '@/libs';
import { useDebounce } from '../../../hooks/useDebounce';
import { FormAutoComplete, type FormAutoCompleteProps } from '../AutoComplete/AutoComplete';
import type { ISelectItem, ISelectItemSection } from '../index';

interface AsyncSearchSelectProps<T extends FieldValues> extends Omit<FormAutoCompleteProps<T>, 'options'> {
  queryKey?: string;
  control: Control<T, unknown>;
  extraSections?: ISelectItemSection[];
  extraOptions?: ISelectItem[];
  groupBy?: 'options' | 'sections';
  entitlementIds?: (string | number)[];
  topicIds?: (string | number)[];
  addValuePrefix?: boolean;
  productId?: EProductType;
  shouldUnregister?: boolean;
  apiSearch?: boolean;
}

export const AsyncSearchSelect = <T extends FieldValues>({
  queryKey,
  control,
  extraSections,
  extraOptions,
  groupBy = 'sections',
  entitlementIds,
  topicIds,
  addValuePrefix = true,
  productId,
  shouldUnregister,
  apiSearch = true,
  ...rest
}: AsyncSearchSelectProps<T>) => {
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 800);

  const { data, loading } = useQuery<IDropdownOptionsData, IDropdownOptionsVar>(DROPDOWN_OPTIONS, {
    variables: {
      force: false,
      search: apiSearch ? debouncedSearchText?.trim() : '',
      table: queryKey ?? 'concepts',
      entitlementIds: entitlementIds?.length ? entitlementIds.map(Number) : undefined,
      topicIds: topicIds?.length ? topicIds.map(Number) : undefined,
      productId: productId ? productId : undefined,
    },
  });

  const sections = useMemo(() => {
    if (groupBy === 'options') {
      return [];
    }
    const opts = data?.admin?.dropdownOptions ?? [];
    const groupedOptions = opts.reduce((acc, opt) => {
      const groupId = opt.metaData?.value ?? opt.value;
      const label = opt.metaData && addValuePrefix ? `[${groupId}] ${opt.metaData.key}` : opt.key;
      if (!groupId) return acc;

      const existingGroup = acc.find((group) => group.label === label);
      const item = {
        key: opt.value as string,
        label: addValuePrefix ? `[${opt.value}] ${opt.key}` : opt.key,
        // description: opt?.description,
      };

      if (existingGroup) {
        existingGroup.items.push(item);
      } else {
        acc.push({ label, items: [item] });
      }

      return acc;
    }, [] as ISelectItemSection[]);

    return [...(extraSections ?? []), ...groupedOptions];
  }, [data, extraSections]);

  const options = useMemo(() => {
    if (groupBy === 'sections') {
      return [];
    }
    const opts = data?.admin?.dropdownOptions ?? [];

    const filteredOptions = opts.map((opt) => ({
      key: opt.value as string,
      label: addValuePrefix ? `[${opt.value}] ${opt.key}` : opt.key,
      description: opt?.description,
    }));

    return [...(extraOptions ?? []), ...filteredOptions];
  }, [data, extraOptions]);

  return (
    <FormAutoComplete
      {...rest}
      // isVirtualized
      options={groupBy === 'options' ? options : []}
      sections={groupBy === 'sections' ? sections : undefined}
      isLoading={loading}
      onInputChange={setSearchText}
      control={control}
      shouldUnregister={shouldUnregister}
    />
  );
};
